import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/providers/AuthProvider';
import { Play, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useMemo, useCallback } from 'react';
import { useCanCreateAssessments, useCanDeleteAssessments } from '@/hooks/usePermissions';
import { cn } from '@/lib/utils';
import {
  DataTable,
  type TableColumn,
  type TableAction,
} from '@/components/shared/data-table';
import type { ApiAssessment } from '@/types/api';
import {
  useAssessmentsQuery,
  useDeleteAssessmentMutation,
} from '@/hooks/queries/useAssessments';
import { usePropertiesQuery } from '@/hooks/queries/useProperties';

export default function Assessments() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { hasRole } = useAuth();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);
  const isAdmin = hasRole('admin') || hasRole('super_admin');

  // Permission checks
  const canCreate = useCanCreateAssessments();
  const canDelete = useCanDeleteAssessments();

  // Filter states
  const [searchTerm, _setSearchTerm] = useState('');
  const [selectedProperty, setSelectedProperty] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [dueDateFilter, setDueDateFilter] = useState<string>('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Delete confirmation dialog state
  const [assessmentToDelete, setAssessmentToDelete] =
    useState<ApiAssessment | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Build API query params from filters
  const queryParams = useMemo(
    () => ({
      page: currentPage,
      page_size: itemsPerPage,
      ...(searchTerm && { search: searchTerm }),
      ...(selectedProperty &&
        selectedProperty !== 'all' && {
          property_id: parseInt(selectedProperty),
        }),
      ...(selectedStatus && {
        status:
          selectedStatus === 'complete'
            ? ('Completed' as const)
            : selectedStatus === 'incomplete'
              ? ('In Progress' as const)
              : undefined,
      }),
      ...(dueDateFilter && { date_from: dueDateFilter }),
    }),
    [currentPage, searchTerm, selectedProperty, selectedStatus, dueDateFilter]
  );

  // Fetch assessments from API
  const { data: assessmentsResponse, isPending } =
    useAssessmentsQuery(queryParams);
  const assessments = assessmentsResponse?.data || [];
  const pagination = assessmentsResponse?.pagination;
  const totalPages = pagination?.last_page || 1;

  // Fetch properties for filter dropdown
  const { data: propertiesResponse } = usePropertiesQuery({ page_size: 100 });

  // Get unique property names for filter dropdown
  const uniqueProperties = useMemo(() => {
    const properties = propertiesResponse?.data || [];
    return properties.map(p => ({ id: p.id.toString(), name: p.name }));
  }, [propertiesResponse?.data]);

  // Delete mutation
  const deleteAssessment = useDeleteAssessmentMutation();

  // Reset to first page when filters change
  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleDeleteAssessment = useCallback(
    (assessment: ApiAssessment) => {
      // Admin and super_admin can delete any assessment, others can only delete drafts
      if (!isAdmin && assessment.status !== 'Draft') {
        toast({
          title: t('common.error', 'Error'),
          description: t(
            'assessments.delete.onlyDrafts',
            'Only draft assessments can be deleted'
          ),
          variant: 'destructive',
          duration: 5000, // Show for 5 seconds
        });
        return;
      }
      setAssessmentToDelete(assessment);
      setIsDeleteDialogOpen(true);
    },
    [t, toast, isAdmin]
  );

  const confirmDelete = useCallback(() => {
    if (assessmentToDelete) {
      deleteAssessment.mutate(assessmentToDelete.id);
      setIsDeleteDialogOpen(false);
      setAssessmentToDelete(null);
    }
  }, [assessmentToDelete, deleteAssessment]);

  const cancelDelete = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setAssessmentToDelete(null);
  }, []);

  const handleNewAssessment = () => {
    navigate('/assessments/create-assessment');
  };

  // Define table columns - NO hiddenOn, all columns visible with horizontal scroll on mobile
  const columns: TableColumn<ApiAssessment>[] = useMemo(
    () => [
      {
        key: 'property_name',
        label: t('assessments.table.propertyName', 'Property'),
        className: 'min-w-[150px]',
        render: assessment => (
          <span className="font-medium text-sm">
            {assessment.property_name || 'N/A'}
          </span>
        ),
      },
      {
        key: 'assigned_to_name',
        label: t('assessments.table.assignedTo', 'Assigned To'),
        className: 'min-w-[120px]',
        render: assessment => (
          <span className="text-sm">
            {assessment.assigned_to_name || 'N/A'}
          </span>
        ),
      },
      {
        key: 'created_by_name',
        label: t('assessments.table.assignedBy', 'Assigned By'),
        className: 'min-w-[120px]',
        render: assessment => (
          <span className="text-sm">
            {assessment.created_by_name || 'N/A'}
          </span>
        ),
      },
      {
        key: 'created_at',
        label: t('assessments.table.dateAssigned', 'Date Assigned'),
        className: 'min-w-[130px]',
        render: assessment => (
          <span className="text-sm whitespace-nowrap">
            {new Date(assessment.created_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </span>
        ),
      },
      {
        key: 'due_date',
        label: t('assessments.table.dueDate', 'Due Date'),
        className: 'min-w-[120px]',
        render: assessment => (
          <span className="font-medium text-orange-600 text-sm whitespace-nowrap">
            {new Date(assessment.due_date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </span>
        ),
      },
      {
        key: 'overall_score',
        label: t('assessments.table.score', 'Score'),
        className: 'text-center min-w-[80px]',
        render: assessment => (
          <span className="font-semibold text-blue-600 text-sm">
            {assessment.overall_score ?? 'N/A'}%
          </span>
        ),
      },
      {
        key: 'completion_percentage',
        label: t('assessments.table.completed', 'Completed'),
        className: 'text-center min-w-[100px]',
        render: assessment => (
          <span className="font-semibold text-sm">
            {assessment.completion_percentage}%
          </span>
        ),
      },
    ],
    [t]
  );

  // Define table actions
  const actions: TableAction<ApiAssessment>[] = useMemo(
    () => [
      {
        label: t('common.start', 'Start'),
        icon: Play,
        onClick: assessment =>
          navigate(`/assessments/perform/${assessment.id}`),
        variant: 'default',
      },
      ...(canDelete
        ? [
            {
              label: t('common.delete', 'Delete'),
              icon: Trash2,
              onClick: handleDeleteAssessment,
              variant: 'destructive' as const,
              separator: true,
            },
          ]
        : []),
    ],
    [t, navigate, handleDeleteAssessment, canDelete]
  );

  // Use shared status color function for consistency

  return (
    <div className={cn('min-h-screen bg-background', isRTL && 'rtl')}>
      <div className="bg-primary text-primary-foreground p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div
            className={cn(
              'flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6',
              isRTL ? 'lg:flex-row-reverse' : 'lg:flex-row'
            )}
          >
            <div className={cn(isRTL && 'text-right')}>
              <h1 className="text-xl sm:text-2xl font-semibold mb-2">
                {t('assessments.title')}
              </h1>
              <div className="text-sm text-primary-foreground/70 mt-1">
                {t('assessments.subtitle')}
              </div>
            </div>
            {canCreate && (
              <Button
                className="bg-accent hover:bg-accent/90 text-accent-foreground w-full lg:w-auto"
                onClick={handleNewAssessment}
              >
                {t('assessments.createAssessment')} +
              </Button>
            )}
          </div>

          {/* Filters Section */}
          <div className="space-y-4">
            {/* Property Filter */}
            <div className="w-full">
              <Select
                value={selectedProperty}
                onValueChange={setSelectedProperty}
              >
                <SelectTrigger
                  className={cn(
                    'w-full sm:w-80 bg-white text-black',
                    isRTL && 'text-right'
                  )}
                >
                  <SelectValue placeholder={t('assessments.selectProperty')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t('assessments.filters.allProperties')}
                  </SelectItem>
                  {uniqueProperties.map(property => (
                    <SelectItem key={property.id} value={property.id}>
                      {property.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date and Status Filters */}
            <div
              className={cn(
                'flex flex-col md:flex-row gap-4',
                isRTL ? 'md:flex-row-reverse' : 'md:flex-row'
              )}
            >
              <div className="flex-1">
                <label
                  className={cn(
                    'text-sm text-primary-foreground/80 block mb-2',
                  )}
                >
                  {t('assessments.filters.date', 'Date')}
                </label>
                <Input
                  type="date"
                  value={dueDateFilter}
                  onChange={e => setDueDateFilter(e.target.value)}
                  className={cn(
                    'bg-white text-black w-full',
                    isRTL && 'text-right'
                  )}
                  placeholder={t('assessments.filters.startDate')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </div>

              <div className="flex-1">
                <label
                  className={cn(
                    'text-sm text-primary-foreground/80 block mb-2',
                  )}
                >
                  {t('assessments.filters.status')}
                </label>
                <div
                  className={cn(
                    'flex gap-2',
                    isRTL ? 'flex-row-reverse justify-end' : 'flex-row justify-start'
                  )}
                >
                  <Button
                    variant={selectedStatus === '' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedStatus('')}
                    className={cn(
                      selectedStatus === ''
                        ? 'bg-white text-black'
                        : 'bg-white/20 text-white border-white/30'
                    )}
                  >
                    {t('assessments.filters.all')}
                  </Button>
                  <Button
                    variant={
                      selectedStatus === 'complete' ? 'default' : 'outline'
                    }
                    size="sm"
                    onClick={() => setSelectedStatus('complete')}
                    className={cn(
                      selectedStatus === 'complete'
                        ? 'bg-white text-black'
                        : 'bg-white/20 text-white border-white/30'
                    )}
                  >
                    {t('assessments.filters.complete')}
                  </Button>
                  <Button
                    variant={
                      selectedStatus === 'incomplete' ? 'default' : 'outline'
                    }
                    size="sm"
                    onClick={() => setSelectedStatus('incomplete')}
                    className={cn(
                      selectedStatus === 'incomplete'
                        ? 'bg-white text-black'
                        : 'bg-white/20 text-white border-white/30'
                    )}
                  >
                    {t('assessments.filters.incomplete')}
                  </Button>
                </div>
              </div>
            </div>

            {/* Reset Filters Button */}
            <div className={cn('flex justify-end', isRTL && 'justify-start')}>
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedProperty('');
                  setSelectedStatus('');
                  setDueDateFilter('');
                  setCurrentPage(1);
                }}
                className="bg-white/20 text-white border-white/30 hover:bg-white/30"
              >
                {t('assessments.filters.reset', 'Reset Filters')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-card p-4 sm:p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Assessments Table with Actions */}
          <Card>
            <DataTable
              data={assessments}
              columns={columns}
              actions={actions}
              isLoading={isPending}
              pagination={{
                current: currentPage,
                total: totalPages,
                count: pagination?.total || 0,
                itemsPerPage: itemsPerPage,
              }}
              onPageChange={handlePageChange}
              emptyMessage={t(
                'assessments.noResults',
                'No assessments match your current filters'
              )}
              tableCaption={t(
                'assessments.manageAssessments',
                'Manage Assessments'
              )}
            />
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('assessments.delete.title', 'Are you sure?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'assessments.delete.description',
                'This action cannot be undone. This will permanently delete the assessment.'
              )}
              {assessmentToDelete && (
                <div className="mt-2 p-2 bg-muted rounded text-sm">
                  <strong>{t('assessments.table.dateAssigned')}:</strong>{' '}
                  {new Date(assessmentToDelete.created_at).toLocaleDateString()}
                  <br />
                  <strong>{t('assessments.table.assignedTo')}:</strong>{' '}
                  {assessmentToDelete.assigned_to_name || 'N/A'}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={cn(isRTL && 'flex-row-reverse')}>
            <AlertDialogCancel onClick={cancelDelete}>
              {t('common.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
